import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { sectors } from "@/data/products";
import { Input } from "@/components/ui/input";
import { Search, ArrowUpRight } from "lucide-react";

export default function Catalog() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return sectors
      .filter((s) => active === "all" || s.slug === active)
      .map((s) => {
        const groups = s.groups
          .map((g) => ({
            ...g,
            items: g.items.filter(
              (it) =>
                !query ||
                it.name.toLowerCase().includes(query) ||
                it.desc.toLowerCase().includes(query) ||
                s.name.toLowerCase().includes(query),
            ),
          }))
          .filter((g) => g.items.length > 0);
        return { ...s, groups };
      })
      .filter((s) => s.groups.length > 0);
  }, [q, active]);

  return (
    <main className="pt-28 pb-24" data-testid="catalog-page">
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Complete Export Catalog</p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tighter leading-[0.95]">
              500+ product lines<br/><em className="text-[#B85C38]">search, filter, request.</em>
            </h1>
          </div>
          <div className="md:col-span-5">
            <div className="relative glass rounded-full border border-[#EAE6DF]">
              <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#9F7A4A]" />
              <Input
                data-testid="catalog-search"
                placeholder='Try "basmati", "turmeric", "marble", "nano urea"'
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-12 pr-5 h-14 rounded-full border-none bg-transparent text-base placeholder:text-[#9F7A4A]/70 focus-visible:ring-0"
              />
            </div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="mt-10 flex gap-2 overflow-x-auto pb-2">
          <button
            data-testid="filter-all"
            onClick={() => setActive("all")}
            className={`shrink-0 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors ${
              active === "all"
                ? "bg-[#2C4532] text-[#F9F8F6] border-[#2C4532]"
                : "bg-white/60 border-[#EAE6DF] text-[#4A4D4A] hover:border-[#2C4532]"
            }`}
          >
            All Sectors
          </button>
          {sectors.map((s) => (
            <button
              key={s.slug}
              data-testid={`filter-${s.slug}`}
              onClick={() => setActive(s.slug)}
              className={`shrink-0 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors ${
                active === s.slug
                  ? "bg-[#2C4532] text-[#F9F8F6] border-[#2C4532]"
                  : "bg-white/60 border-[#EAE6DF] text-[#4A4D4A] hover:border-[#2C4532]"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mt-16 space-y-20">
        {filtered.length === 0 && (
          <div className="text-center py-20" data-testid="catalog-empty">
            <p className="font-serif text-3xl text-[#2C4532]">No products found</p>
            <p className="mt-3 text-[#4A4D4A]">Try a different keyword or clear your search to browse all sectors.</p>
          </div>
        )}

        {filtered.map((s) => (
          <div key={s.slug} data-testid={`catalog-sector-${s.slug}`}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A]">Sector {s.index}</p>
                <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#2C4532] mt-2">{s.name}</h2>
                <p className="text-[#4A4D4A] mt-2 max-w-2xl">{s.tagline}</p>
              </div>
              <Link
                to={`/sectors/${s.slug}`}
                data-testid={`catalog-view-${s.slug}`}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#2C4532] hover:text-[#B85C38]"
              >
                View sector <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {s.groups.map((g) => (
                <div key={g.title} className="glass rounded-3xl border border-[#EAE6DF] p-6">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#B85C38] font-medium">{g.title}</p>
                  <ul className="mt-5 space-y-4">
                    {g.items.map((it) => (
                      <li key={it.name} className="border-b border-[#EAE6DF] pb-3 last:border-0 last:pb-0">
                        <div className="font-serif text-lg text-[#1A1B1A]">{it.name}</div>
                        <div className="text-sm text-[#4A4D4A] font-light mt-1 leading-relaxed">{it.desc}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>
    </main>
  );
}
